const API_BASE = `${import.meta.env.BASE_URL || "/"}api`;

/**
 * Maintenance-mode bypass client.
 *
 * The holding page (MaintenancePage) lets an operator enter the shared
 * `SITE_GATE_PASSWORD` to reveal the full live site for testing during an
 * upgrade. The password is validated server-side; on success the server sets
 * an HttpOnly `maint_bypass` cookie, and `getMaintenanceAccess()` reports
 * whether the current browser holds a valid bypass.
 */

export async function getMaintenanceAccess(): Promise<boolean> {
  try {
    const res = await fetch(`${API_BASE}/maintenance/access`, {
      headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) return false;
    const body = (await res.json()) as { unlocked?: boolean };
    return body.unlocked === true;
  } catch {
    return false;
  }
}

export interface MaintenanceUnlockResult {
  ok: boolean;
  /** "incorrect" for a bad password, "unavailable" when not configured, "error" otherwise. */
  reason?: "incorrect" | "unavailable" | "error";
}

export async function unlockMaintenance(
  password: string,
): Promise<MaintenanceUnlockResult> {
  try {
    const res = await fetch(`${API_BASE}/maintenance/unlock`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    });
    if (res.ok) return { ok: true };
    if (res.status === 401) return { ok: false, reason: "incorrect" };
    if (res.status === 503) return { ok: false, reason: "unavailable" };
    return { ok: false, reason: "error" };
  } catch {
    return { ok: false, reason: "error" };
  }
}
